import { useEffect } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { ArrayItemsEditor } from './ArrayItemsEditor';
import type { PropertyData } from '@/domain/entities/property-object';

import {
  CoolingFuelSource,
  CoolingType,
  HeatingFuelSource,
  HeatingOrCoolingCoverage,
  HeatingType,
  HomeSystemTypeConst,
  HvacFuelSource,
  HvacSystemType,
  PipeCondition,
  PlumbingType,
  SepticMaintenanceFrequency,
  SewageType,
  WastePickupLocation,
  WaterHeaterFuelSource,
  WaterHeaterType,
  WaterSource,
  type HomeSystemType,
  type ICoolingSystemConfig,
  type IElectricalSystemConfig,
  type IHeatingSystemConfig,
  type IHomeSystem,
  type IHomeSystemConfigBase,
  type IHvacSystemConfig,
  type IPlumbingSystemConfig,
  type ISewageSystemConfig,
  type IWasteSystemConfig,
  type IWaterSystemConfig,
  type SystemCondition,
  type SystemConfigurationMap,
} from '@/domain/entities/system';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { RootState } from '@/store/root-state';

import { inputValueToUnixSeconds, sortItems, unixSecondsToInputValue } from '@/lib';
import { PerItemSaveActions } from './shared/PerItemSaveActions';
import {
  buildPropertyEditorPersistKey,
  getDirtyBorderClass,
  isTempId,
  reconcileKeepOrder,
  removeAtIndex,
} from './shared/helpers';
import {
  markPropertySystemClean,
  markPropertySystemDirty,
  selectPropertySystemsActiveSort,
  selectPropertySystemsByPropertyId,
  selectPropertySystemsDirtyByPropertyId,
  selectPropertySystemsFetchedByPropertyId,
  selectPropertySystemsListingByPropertyId,
  setPropertySystemsActiveSort,
  setPropertySystemsForProperty,
} from '@/features/systems/systemsSlice';
import {
  createPropertySystem,
  deletePropertySystem,
  fetchPropertySystemsByPropertyId,
  updatePropertySystem,
} from '@/features/systems/systemsThunks';
import { useSortedItemsOnce } from './shared/useSortedItemsOnce';
import { usePropertyEntityTabController } from './shared/usePropertyEntityTabController';
import { TabLoadBanner } from './shared/TabLoadBanner';
import { TypeNames } from '@/domain/shared/storable';
import { usePermissions } from '@/hooks/usePermissions';
import { Permissions } from '@/lib/permissions';

type BuilderMode = 'create' | 'edit';

function nowSec() {
  return Math.floor(Date.now() / 1000);
}

function toNumberOrUndefined(v: string): number | undefined {
  if (v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

const CONDITIONS: readonly SystemCondition[] = ['new', 'good', 'fair', 'poor', 'unknown'] as const;

function makeBaseConfig() {
  return {
    isPresent: true,
    yearInstalled: undefined,
    yearUpdated: undefined,
    lastInspectedDate: undefined,
    lastServiced: undefined,
    condition: 'unknown' as SystemCondition,
    notes: '',
    brandID: null as string | null,
    model: '',
  };
}

function normalizeConfigForType<T extends keyof SystemConfigurationMap>(
  type: T,
  prev: SystemConfigurationMap[T] | undefined,
): SystemConfigurationMap[T] {
  switch (type) {
    case 'electrical': {
      const p = prev as IElectricalSystemConfig | undefined;
      return {
        ...makeBaseConfig(),
        ...p,
        amperage: p?.amperage ?? 100,
        circuitCount: p?.circuitCount,
        subPanels: p?.subPanels,
      } as SystemConfigurationMap[T];
    }

    case 'heating': {
      const p = prev as IHeatingSystemConfig | undefined;
      return {
        ...makeBaseConfig(),
        ...p,
        type: p?.type ?? HeatingType.FURNACE,
        fuelSource: p?.fuelSource ?? HeatingFuelSource.NATURAL_GAS,
        coverage: p?.coverage ?? HeatingOrCoolingCoverage.WHOLE_HOUSE,
      } as SystemConfigurationMap[T];
    }

    case 'cooling': {
      const p = prev as ICoolingSystemConfig | undefined;
      return {
        ...makeBaseConfig(),
        ...p,
        type: p?.type ?? CoolingType.CENTRAL_AC,
        fuelSource: p?.fuelSource ?? CoolingFuelSource.ELECTRICITY,
        coverage: p?.coverage ?? HeatingOrCoolingCoverage.WHOLE_HOUSE,
      } as SystemConfigurationMap[T];
    }

    case 'hvac': {
      const p = prev as IHvacSystemConfig | undefined;
      return {
        ...makeBaseConfig(),
        ...p,
        type: p?.type ?? HvacSystemType.SPLIT_SYSTEM,
        fuelSource: p?.fuelSource ?? HvacFuelSource.NATURAL_GAS,
        coverage: p?.coverage ?? HeatingOrCoolingCoverage.WHOLE_HOUSE,
      } as SystemConfigurationMap[T];
    }

    case 'water': {
      const p = prev as IWaterSystemConfig | undefined;
      return {
        ...makeBaseConfig(),
        ...p,
        source: p?.source ?? WaterSource.MUNICIPAL,
        waterHeaterType: p?.waterHeaterType,
        waterHeaterFuel: p?.waterHeaterFuel,
      } as SystemConfigurationMap[T];
    }

    case 'waste': {
      const p = prev as IWasteSystemConfig | undefined;
      return {
        ...makeBaseConfig(),
        ...p,
        pickupDays: p?.pickupDays ?? '',
        includesRecycling: p?.includesRecycling ?? false,
        includesCompost: p?.includesCompost ?? false,
        location: p?.location ?? WastePickupLocation.CURBSIDE,
        bulkPickupInfo: p?.bulkPickupInfo ?? '',
      } as SystemConfigurationMap[T];
    }

    case 'sewage': {
      const p = prev as ISewageSystemConfig | undefined;
      return {
        ...makeBaseConfig(),
        ...p,
        type: p?.type ?? SewageType.CITY,
        lastPumpedDate: p?.lastPumpedDate,
        maintenanceFrequency: p?.maintenanceFrequency,
      } as SystemConfigurationMap[T];
    }

    case 'plumbing': {
      const p = prev as IPlumbingSystemConfig | undefined;
      return {
        ...makeBaseConfig(),
        ...p,
        type: p?.type ?? PlumbingType.PEX,
        pipeCondition: p?.pipeCondition ?? PipeCondition.Good,
        mainShutoffLocation: p?.mainShutoffLocation ?? '',
        waterMeterLocation: p?.waterMeterLocation ?? '',
      } as SystemConfigurationMap[T];
    }
  }

  return makeBaseConfig() as SystemConfigurationMap[T];
}

const patchSystem = (item: IHomeSystem, patch: Partial<IHomeSystem>): IHomeSystem => ({
  ...item,
  ...patch,
  updatedAt: nowSec(),
});

function makeNewSystem(propertyID: string): IHomeSystem {
  const t = nowSec();
  const systemType = HomeSystemTypeConst.Other as HomeSystemType;

  return {
    id: `tmp-system-${t}`,
    typeName: TypeNames.System,
    createdAt: t,
    updatedAt: t,
    title: 'New System',
    description: null,
    thumbnail: null,
    thumbnailUrl: null,
    photos: null,
    propertyID,
    systemType,
    configuration: normalizeConfigForType(systemType, undefined),
  } as IHomeSystem;
}

function SystemEditorCard(props: {
  item: IHomeSystem;

  mode: BuilderMode;
  resolvedPropertyID: string;

  setItem: (next: IHomeSystem) => void;
  markDirty: (id: string) => void;
}) {
  const { item, mode, resolvedPropertyID, setItem, markDirty } = props;

  const set = (patch: Partial<IHomeSystem>) => {
    const next = patchSystem(item, patch);
    setItem(next);

    if (mode !== 'edit' || !next.id || !resolvedPropertyID) return;

    markDirty(next.id);
  };

  const baseCfg = item.configuration as IHomeSystemConfigBase;

  const setBaseCfg = (patch: Partial<IHomeSystemConfigBase>) => {
    set({
      configuration: {
        ...item.configuration,
        ...patch,
      } as typeof item.configuration,
    });
  };

  const onSystemTypeChange = (nextType: keyof SystemConfigurationMap) => {
    set({
      systemType: nextType,
      configuration: normalizeConfigForType(
        nextType as keyof SystemConfigurationMap,
        item.configuration as SystemConfigurationMap[typeof nextType],
      ),
    });
  };

  return (
    <div className="space-y-6">
      {/* Title + Type */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Title</Label>
          <Input
            placeholder="e.g., Electrical Panel"
            value={item.title ?? ''}
            onChange={(e) => set({ title: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label>System Type</Label>
          <Select value={item.systemType} onValueChange={(v) => onSystemTypeChange(v as keyof SystemConfigurationMap)}>
            <SelectTrigger>
              <SelectValue placeholder="Select system type" />
            </SelectTrigger>
            <SelectContent>
              {Object.values(HomeSystemTypeConst).map((v) => (
                <SelectItem key={v} value={v}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2 md:col-span-2">
        <Label>Description</Label>
        <Input
          placeholder="Optional notes (e.g., provider, plan, meter info)"
          value={item.description ?? ''}
          onChange={(e) => set({ description: e.target.value || null })}
        />
      </div>

      {/* Base config */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <Label>isPresent</Label>
          <Select
            value={String(baseCfg.isPresent ?? true)}
            onValueChange={(v) => setBaseCfg({ isPresent: v === 'true' })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">true</SelectItem>
              <SelectItem value="false">false</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Condition</Label>
          <Select
            value={(baseCfg.condition ?? 'unknown') as SystemCondition}
            onValueChange={(condition) => setBaseCfg({ condition: condition as SystemCondition })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select condition" />
            </SelectTrigger>
            <SelectContent>
              {CONDITIONS.map((v) => (
                <SelectItem key={v} value={v}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Year Installed</Label>
          <Input
            type="datetime-local"
            placeholder="Not set"
            value={baseCfg.yearInstalled ? unixSecondsToInputValue(baseCfg.yearInstalled, 'datetime') : ''}
            onChange={(e) => setBaseCfg({ yearInstalled: inputValueToUnixSeconds(e.target.value, 'datetime') })}
          />
        </div>

        <div className="space-y-2">
          <Label>Year Updated</Label>
          <Input
            type="datetime-local"
            placeholder="Not set"
            value={baseCfg.yearUpdated ? unixSecondsToInputValue(baseCfg.yearUpdated, 'datetime') : ''}
            onChange={(e) => setBaseCfg({ yearUpdated: inputValueToUnixSeconds(e.target.value, 'datetime') })}
          />
        </div>

        <div className="space-y-2">
          <Label>Model</Label>
          <Input
            placeholder="Not set"
            value={baseCfg.model ?? ''}
            onChange={(e) => setBaseCfg({ model: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label>Brand ID</Label>
          <Input
            placeholder="Not set"
            value={baseCfg.brandID ?? ''}
            onChange={(e) => setBaseCfg({ brandID: e.target.value || null })}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea
          placeholder="Not set"
          value={baseCfg.notes ?? ''}
          onChange={(e) => setBaseCfg({ notes: e.target.value })}
        />
      </div>

      {/* Type-specific config blocks: (copied as-is from your legacy tab) */}
      {item.systemType === HomeSystemTypeConst.Electrical
        ? (() => {
            const cfg = item.configuration as IElectricalSystemConfig;
            const setCfg = (patch: Partial<IElectricalSystemConfig>) =>
              set({ configuration: { ...cfg, ...patch } as typeof item.configuration });

            return (
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Amperage</Label>
                  <Input
                    type="number"
                    value={cfg.amperage ?? ''}
                    onChange={(e) => setCfg({ amperage: toNumberOrUndefined(e.target.value) ?? 0 })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Circuit Count</Label>
                  <Input
                    type="number"
                    value={cfg.circuitCount ?? ''}
                    onChange={(e) => setCfg({ circuitCount: toNumberOrUndefined(e.target.value) })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Sub Panels</Label>
                  <Input
                    type="number"
                    value={cfg.subPanels ?? ''}
                    onChange={(e) => setCfg({ subPanels: toNumberOrUndefined(e.target.value) })}
                  />
                </div>
              </div>
            );
          })()
        : null}

      {item.systemType === HomeSystemTypeConst.Heating
        ? (() => {
            const cfg = item.configuration as IHeatingSystemConfig;
            const setCfg = (patch: Partial<IHeatingSystemConfig>) =>
              set({ configuration: { ...cfg, ...patch } as typeof item.configuration });

            return (
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Heating Type</Label>
                  <Select value={cfg.type} onValueChange={(v) => setCfg({ type: v as HeatingType })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(HeatingType).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Fuel Source</Label>
                  <Select value={cfg.fuelSource} onValueChange={(v) => setCfg({ fuelSource: v as HeatingFuelSource })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(HeatingFuelSource).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Coverage</Label>
                  <Select
                    value={cfg.coverage ?? HeatingOrCoolingCoverage.WHOLE_HOUSE}
                    onValueChange={(v) => setCfg({ coverage: v as HeatingOrCoolingCoverage })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(HeatingOrCoolingCoverage).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })()
        : null}

      {item.systemType === HomeSystemTypeConst.Cooling
        ? (() => {
            const cfg = item.configuration as ICoolingSystemConfig;
            const setCfg = (patch: Partial<ICoolingSystemConfig>) =>
              set({ configuration: { ...cfg, ...patch } as typeof item.configuration });

            return (
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Cooling Type</Label>
                  <Select value={cfg.type} onValueChange={(v) => setCfg({ type: v as CoolingType })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(CoolingType).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Fuel Source</Label>
                  <Select value={cfg.fuelSource} onValueChange={(v) => setCfg({ fuelSource: v as CoolingFuelSource })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(CoolingFuelSource).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Coverage</Label>
                  <Select
                    value={cfg.coverage ?? HeatingOrCoolingCoverage.WHOLE_HOUSE}
                    onValueChange={(v) => setCfg({ coverage: v as HeatingOrCoolingCoverage })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(HeatingOrCoolingCoverage).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })()
        : null}

      {item.systemType === HomeSystemTypeConst.Hvac
        ? (() => {
            const cfg = item.configuration as IHvacSystemConfig;
            const setCfg = (patch: Partial<IHvacSystemConfig>) =>
              set({ configuration: { ...cfg, ...patch } as typeof item.configuration });

            return (
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>HVAC Type</Label>
                  <Select value={cfg.type} onValueChange={(v) => setCfg({ type: v as HvacSystemType })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(HvacSystemType).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Fuel Source</Label>
                  <Select value={cfg.fuelSource} onValueChange={(v) => setCfg({ fuelSource: v as HvacFuelSource })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(HvacFuelSource).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Coverage</Label>
                  <Select
                    value={cfg.coverage ?? HeatingOrCoolingCoverage.WHOLE_HOUSE}
                    onValueChange={(v) => setCfg({ coverage: v as HeatingOrCoolingCoverage })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(HeatingOrCoolingCoverage).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })()
        : null}

      {item.systemType === HomeSystemTypeConst.Water
        ? (() => {
            const cfg = item.configuration as IWaterSystemConfig;
            const setCfg = (patch: Partial<IWaterSystemConfig>) =>
              set({ configuration: { ...cfg, ...patch } as typeof item.configuration });

            return (
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Water Source</Label>
                  <Select value={cfg.source} onValueChange={(v) => setCfg({ source: v as WaterSource })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(WaterSource).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Water Heater Type</Label>
                  <Select
                    value={cfg.waterHeaterType ?? WaterHeaterType.TANK}
                    onValueChange={(v) => setCfg({ waterHeaterType: v as WaterHeaterType })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(WaterHeaterType).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Water Heater Fuel</Label>
                  <Select
                    value={cfg.waterHeaterFuel ?? WaterHeaterFuelSource.NATURAL_GAS}
                    onValueChange={(v) => setCfg({ waterHeaterFuel: v as WaterHeaterFuelSource })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(WaterHeaterFuelSource).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })()
        : null}

      {item.systemType === HomeSystemTypeConst.Sewage
        ? (() => {
            const cfg = item.configuration as ISewageSystemConfig;
            const setCfg = (patch: Partial<ISewageSystemConfig>) =>
              set({ configuration: { ...cfg, ...patch } as typeof item.configuration });

            return (
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Sewage Type</Label>
                  <Select value={cfg.type} onValueChange={(v) => setCfg({ type: v as SewageType })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(SewageType).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Last Pumped Date</Label>
                  <Input
                    type="date"
                    value={cfg.lastPumpedDate ? unixSecondsToInputValue(cfg.lastPumpedDate, 'date') : ''}
                    onChange={(e) => setCfg({ lastPumpedDate: inputValueToUnixSeconds(e.target.value, 'date') })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Maintenance Frequency</Label>
                  <Select
                    value={cfg.maintenanceFrequency ?? SepticMaintenanceFrequency.AS_NEEDED}
                    onValueChange={(v) => setCfg({ maintenanceFrequency: v as SepticMaintenanceFrequency })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(SepticMaintenanceFrequency).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })()
        : null}

      {item.systemType === HomeSystemTypeConst.Waste
        ? (() => {
            const cfg = item.configuration as IWasteSystemConfig;
            const setCfg = (patch: Partial<IWasteSystemConfig>) =>
              set({ configuration: { ...cfg, ...patch } as typeof item.configuration });

            return (
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Pickup Days</Label>
                  <Input value={cfg.pickupDays ?? ''} onChange={(e) => setCfg({ pickupDays: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Pickup Location</Label>
                  <Select
                    value={cfg.location ?? WastePickupLocation.CURBSIDE}
                    onValueChange={(v) => setCfg({ location: v as WastePickupLocation })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(WastePickupLocation).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Bulk Pickup Info</Label>
                  <Input
                    value={cfg.bulkPickupInfo ?? ''}
                    onChange={(e) => setCfg({ bulkPickupInfo: e.target.value })}
                  />
                </div>
              </div>
            );
          })()
        : null}

      {item.systemType === HomeSystemTypeConst.Plumbing
        ? (() => {
            const cfg = item.configuration as IPlumbingSystemConfig;
            const setCfg = (patch: Partial<IPlumbingSystemConfig>) =>
              set({ configuration: { ...cfg, ...patch } as typeof item.configuration });

            return (
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Plumbing Type</Label>
                  <Select value={cfg.type} onValueChange={(v) => setCfg({ type: v as PlumbingType })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(PlumbingType).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Pipe Condition</Label>
                  <Select
                    value={cfg.pipeCondition ?? PipeCondition.Good}
                    onValueChange={(v) => setCfg({ pipeCondition: v as PipeCondition })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(PipeCondition).map((v) => (
                        <SelectItem key={v} value={v}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Main Shutoff Location</Label>
                  <Input
                    value={cfg.mainShutoffLocation ?? ''}
                    onChange={(e) => setCfg({ mainShutoffLocation: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Water Meter Location</Label>
                  <Input
                    value={cfg.waterMeterLocation ?? ''}
                    onChange={(e) => setCfg({ waterMeterLocation: e.target.value })}
                  />
                </div>
              </div>
            );
          })()
        : null}
    </div>
  );
}
export function SystemsTab(props: {
  data: PropertyData;
  setData: (next: PropertyData) => void;
  mode: BuilderMode;
  propertyId?: string;
}) {
  const { data, setData, mode, propertyId } = props;
  const dispatch = useAppDispatch();
  const { hasPermission, isSuperAdmin } = usePermissions();
  const canCreate = mode === 'create' || isSuperAdmin || hasPermission(Permissions.SystemsCreate);
  const canUpdate = mode === 'create' || isSuperAdmin || hasPermission(Permissions.SystemsUpdate);
  const canDelete = mode === 'create' || isSuperAdmin || hasPermission(Permissions.SystemsDelete);

  const activeSort = useAppSelector(selectPropertySystemsActiveSort);
  const resolvedPropertyID = data.property?.id ?? propertyId ?? '';
  const dirtyById = useAppSelector((s) => selectPropertySystemsDirtyByPropertyId(s, resolvedPropertyID));
  const localItems = data.systems ?? [];
  const storeItems = useAppSelector((s) => selectPropertySystemsByPropertyId(s, resolvedPropertyID));
  const effectiveItems = mode === 'edit' ? storeItems : localItems;
  const isLoading = useAppSelector((s) => selectPropertySystemsListingByPropertyId(s, resolvedPropertyID));
  const hasFetched = useAppSelector((s) => selectPropertySystemsFetchedByPropertyId(s, resolvedPropertyID));
  const error = useAppSelector((s: RootState) =>
    resolvedPropertyID ? (s.propertySystems?.errorByPropertyID?.[resolvedPropertyID] ?? null) : null,
  );

  const scopeKey = `${mode}:${resolvedPropertyID || 'new'}`;
  const { sortedItems, setSortedItems, resort } = useSortedItemsOnce<IHomeSystem>({
    scopeKey,
    items: effectiveItems,
    enabled: mode === 'edit' ? hasFetched : false,
    activeSort,
    sort: (arr, spec) => sortItems(arr, spec) as IHomeSystem[],
    reconcile: (prev, next) => reconcileKeepOrder(prev, next, { insertNew: 'top' }) as IHomeSystem[],
  });

  const markDirty = (id: string) => {
    if (!resolvedPropertyID) return;
    dispatch(markPropertySystemDirty({ propertyID: resolvedPropertyID, systemID: id }));
  };

  const markClean = (id: string) => {
    if (!resolvedPropertyID) return;
    dispatch(markPropertySystemClean({ propertyID: resolvedPropertyID, id }));
  };

  const ctrl = usePropertyEntityTabController<IHomeSystem>({
    mode,
    resolvedPropertyID,

    localItems,
    storeItems,

    writeLocal: (next) => setData({ ...data, systems: next }),
    writeStore: (next) => dispatch(setPropertySystemsForProperty({ propertyID: resolvedPropertyID, items: next })),

    hasFetched,
    fetchRemote: () => dispatch(fetchPropertySystemsByPropertyId({ propertyID: resolvedPropertyID })),

    markDirty,
    markClean,

    createRemote: (sys, tempId) => dispatch(createPropertySystem({ system: sys, tempId })).unwrap(),
    updateRemote: (sys) =>
      dispatch(updatePropertySystem({ id: sys.id!, propertyID: resolvedPropertyID, patch: sys })).unwrap(),
    deleteRemote: (id) => dispatch(deletePropertySystem({ id, propertyID: resolvedPropertyID })).unwrap(),

    entityName: 'system',
    isTempId, // from helpers.ts

    normalizeBeforeSave: (sys) => ({
      ...sys,
      propertyID: sys.propertyID ?? resolvedPropertyID,
      updatedAt: nowSec(),
    }),
  });

  useEffect(() => {
    if (mode !== 'edit') return;
    if (!resolvedPropertyID) return;
    if (hasFetched) return;
    void dispatch(fetchPropertySystemsByPropertyId({ propertyID: resolvedPropertyID }));
  }, [dispatch, mode, resolvedPropertyID, hasFetched]);

  return (
    <div className="space-y-3">
      <TabLoadBanner mode={mode} isLoading={isLoading} error={error} loadingLabel="Loading systems…" />
      <ArrayItemsEditor<IHomeSystem>
        title="Systems"
        persistKey={buildPropertyEditorPersistKey({ propertyID: resolvedPropertyID, tab: 'systems' })}
        items={sortedItems}
        onChange={ctrl.writeAll}
        createItem={() => makeNewSystem(resolvedPropertyID || 'unknown-property')}
        canAdd={canCreate}
        canEdit={(item) => (isTempId(item.id) ? canCreate : canUpdate)}
        canRemove={(item) => (isTempId(item.id) ? canCreate : canDelete)}
        getItemTitle={(x, idx) => x.title || `System #${idx + 1}`}
        sortItems={(_items, sortSpec) => {
          setSortedItems(sortItems(_items, sortSpec) as IHomeSystem[]);
          dispatch(setPropertySystemsActiveSort(sortSpec));
        }}
        resortItems={resort}
        activeSort={activeSort}
        getItemClassName={(sys) =>
          getDirtyBorderClass({
            mode,
            id: sys.id,
            dirtyById,
            isTempId,
          })
        }
        rightHeader={({ item }) => {
          if (mode !== 'edit' || !item.id) return null;

          const id = item.id;
          const saving = !!ctrl.savingById[id];
          const canSave = (dirtyById[id] ?? false) || isTempId(id);
          const err = ctrl.saveErrorById[id];
          const mayCreateOrUpdate = isTempId(id) ? canCreate : canUpdate;
          const mayDelete = isTempId(id) ? canCreate : canDelete;

          if (!mayCreateOrUpdate && !mayDelete && !err) return null;

          return (
            <PerItemSaveActions
              error={err}
              saving={saving}
              canSave={canSave}
              saveLabel={isTempId(id) ? 'Save' : 'Update'}
              onDelete={mayDelete ? () => void ctrl.deleteItem(item) : undefined}
              onSave={mayCreateOrUpdate ? () => void ctrl.saveItem(item) : undefined}
            />
          );
        }}
        onRemove={(item, idx) => {
          if (mode === 'edit') {
            void ctrl.deleteItem(item);
            return;
          }
          setData({ ...data, systems: removeAtIndex(data.systems, idx) });
        }}
        onRefresh={() => {
          if (mode !== 'edit') return;
          if (!resolvedPropertyID) return;
          void dispatch(fetchPropertySystemsByPropertyId({ propertyID: resolvedPropertyID }));
        }}
        isRefreshing={isLoading}
        renderItem={({ item, setItem }) => (
          <SystemEditorCard
            item={item}
            mode={mode}
            resolvedPropertyID={resolvedPropertyID}
            setItem={setItem}
            markDirty={markDirty}
          />
        )}
      />
    </div>
  );
}
